import React, { PropsWithChildren } from 'react';
import { Body, Box } from '@hover/blueprint';
import { StaticImage } from 'gatsby-plugin-image';
import { iCamera, iHeart, iImage, iMessageSquare } from '@hover/icons';
import type { IHomeownerRenderingStep } from '../interfaces/IHomeownerRenderingStep';
import type { IHomeownerRenderingsInspired } from '../interfaces/IHomeownerRenderingsInspired';

export const HOMEOWNER_RENDERINGS_HERO_IMAGES = [
  {
    key: 2,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-10.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="eager"
        quality={80}
      />
    ),
  },
  {
    key: 3,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-9.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="eager"
        quality={80}
      />
    ),
  },
  {
    key: 4,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-8.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="lazy"
        quality={80}
      />
    ),
  },
  {
    key: 5,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-7.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="lazy"
        quality={80}
      />
    ),
  },
  {
    key: 6,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-6.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="lazy"
        quality={80}
      />
    ),
  },
  {
    key: 7,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-5.jpg"
        alt=""
        loading="lazy"
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        quality={80}
      />
    ),
  },
  {
    key: 8,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-4.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="lazy"
        quality={80}
      />
    ),
  },
  {
    key: 9,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-3.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="lazy"
        quality={80}
      />
    ),
  },
  {
    key: 10,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-2.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="lazy"
        quality={80}
      />
    ),
  },
  {
    key: 11,
    image: (
      <StaticImage
        src="../assets/images/homeowner_hero-1.jpg"
        alt=""
        style={{ width: '100%', height: '100%' }}
        objectFit="cover"
        loading="lazy"
        quality={80}
      />
    ),
  },
];

export const HOMEOWNER_RENDERING_STEPS: IHomeownerRenderingStep[] = [
  {
    key: 0,
    icon: iCamera,
    title: 'Take a photo',
    description: 'Share an image (or blueprint) of your home with us.',
  },
  {
    key: 1,
    icon: iMessageSquare,
    title: 'Tell us about your style',
    description: 'Take our style quiz and give us some project details.',
  },
  {
    key: 2,
    icon: iImage,
    title: 'Get your designs',
    description: 'We craft personalized designs of your home.',
  },
  {
    key: 2,
    icon: iHeart,
    title: 'Show it off',
    description: 'Send to your friends, family, contractor to get feedback.',
  },
];

export const SlideBadge: React.FC<PropsWithChildren<{}>> = ({ children }) => (
  <Body
    size={300}
    isBold
    position="absolute"
    top={5}
    right={6}
    borderRadius={4}
    padding="8px 16px"
    bgColor="rgba(0, 0, 0, 0.65)"
    color="white"
    height="auto !important"
  >
    {children}
  </Body>
);

export const HOMEOWNER_RENDERINGS_RETHINK_SLIDES = [
  {
    image: (
      <Box position="relative">
        <StaticImage
          src="../assets/images/homeowner_rethink_1.jpg"
          alt=""
          objectFit="cover"
        />
        <SlideBadge>Before</SlideBadge>
      </Box>
    ),
    thumbnail: (
      <StaticImage
        src="../assets/images/homeowner_rethink_thumb_1.jpg"
        alt=""
      />
    ),
    key: 0,
  },
  {
    image: (
      <Box position="relative">
        <StaticImage
          src="../assets/images/homeowner_rethink_2.jpg"
          alt=""
          objectFit="cover"
        />
        <SlideBadge>After 1</SlideBadge>
      </Box>
    ),
    thumbnail: (
      <StaticImage
        src="../assets/images/homeowner_rethink_thumb_2.jpg"
        alt=""
      />
    ),
    key: 1,
  },
  {
    image: (
      <Box position="relative">
        <StaticImage
          src="../assets/images/homeowner_rethink_3.jpg"
          alt=""
          objectFit="cover"
        />
        <SlideBadge>After 2</SlideBadge>
      </Box>
    ),
    thumbnail: (
      <StaticImage
        src="../assets/images/homeowner_rethink_thumb_3.jpg"
        alt=""
      />
    ),
    key: 2,
  },
  {
    image: (
      <Box position="relative">
        <StaticImage
          src="../assets/images/homeowner_rethink_4.jpg"
          alt=""
          objectFit="cover"
        />
        <SlideBadge>After 3</SlideBadge>
      </Box>
    ),
    thumbnail: (
      <StaticImage
        src="../assets/images/homeowner_rethink_thumb_4.jpg"
        alt=""
      />
    ),
    key: 3,
  },
];

const InspiredSlideWrapper: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => (
  <Box borderRadius={4} flexDirection={{ base: 'column', tablet: 'row' }}>
    {children}
  </Box>
);
const InspiredImageWrapper: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => (
  <Box position="relative" h={{ base: '250px', tablet: '400px' }}>
    {children}
  </Box>
);

export const HOMEOWNER_RENDERINGS_INSPIRED_SLIDES: IHomeownerRenderingsInspired[] =
  [
    {
      message: {
        text: '“We saw the rendering from Hover… and we were like, oh that actually looks really good, maybe the shingles would work for us. It helped us open up to the idea of mixing and matching different things, because otherwise we were just going to go with all one thing.”\n',
        author: {
          name: 'Jackie',
          position: 'Indiana',
        },
      },
      cover: (
        <InspiredSlideWrapper>
          <InspiredImageWrapper>
            <StaticImage
              src="../assets/images/homeowner_inspired_before_2.jpg"
              alt=""
            />
            <SlideBadge>Before</SlideBadge>
          </InspiredImageWrapper>
          <InspiredImageWrapper>
            <StaticImage
              src="../assets/images/homeowner_inspired_after_2.jpg"
              alt=""
            />
            <SlideBadge>After</SlideBadge>
          </InspiredImageWrapper>
        </InspiredSlideWrapper>
      ),
      key: 1,
    },
    {
      message: {
        text: '“When you have multiple parties deciding on designs, the more likely you are to disagree. I enjoyed having the flexibility to explore different styles and colors. When I got my designs back, I knew I was going to be able to find that common ground.”\n',
        author: {
          name: 'John',
          position: 'North Carolina',
        },
      },
      cover: (
        <InspiredSlideWrapper>
          <InspiredImageWrapper>
            <StaticImage
              src="../assets/images/homeowner_inspired_before_3.jpg"
              alt=""
            />
            <SlideBadge>Before</SlideBadge>
          </InspiredImageWrapper>
          <InspiredImageWrapper>
            <StaticImage
              src="../assets/images/homeowner_inspired_after_3.jpg"
              alt=""
            />
            <SlideBadge>After</SlideBadge>
          </InspiredImageWrapper>
        </InspiredSlideWrapper>
      ),
      key: 2,
    },
    {
      message: {
        text: '“Using HOVER was like a ‘Thank God moment’. There are options we love and the design looked really good… we’ve wondered if this can be a long term home and after seeing [HOVER’s design] we were like, ‘yes it can be!’”',
        author: {
          name: 'Sarah',
          position: 'Tennessee',
        },
      },
      cover: (
        <InspiredSlideWrapper>
          <InspiredImageWrapper>
            <StaticImage
              src="../assets/images/homeowner_inspired_before_4.jpg"
              alt=""
            />
            <SlideBadge>Before</SlideBadge>
          </InspiredImageWrapper>
          <InspiredImageWrapper>
            <StaticImage
              src="../assets/images/homeowner_inspired_after_4.jpg"
              alt=""
            />
            <SlideBadge>After</SlideBadge>
          </InspiredImageWrapper>
        </InspiredSlideWrapper>
      ),
      key: 3,
    },
  ];
